import React, { useCallback, useEffect, useMemo, useState } from "react";
import QRCode from "qrcode";
import { School } from "models/school";
import { Tournament } from "models/tournament";
import { Photo } from "models/photo";
import { getAllSchoolUnionTournaments } from "services/schoolUnion/tournaments";
import { getSchoolBigscreenPhotos } from "services/school";
import { getNews } from "services/news";
import { getAgesGroupFormatted } from "helpers/accessors";
import { getTournamentTVPublicSiteLink } from "helpers/link";
import "./UnionBigScreen.scss";
import "./UnionBigScreenFixes.scss";

type Slide = {
  id: string;
  eyebrow: string;
  title: string;
  subtitle: string;
  kind: string;
  items: any[];
};
const safe = <T,>(request: Promise<T>, fallback: T) =>
  request.catch(() => fallback);
const fmt = (value: string, options: Intl.DateTimeFormatOptions) =>
  new Intl.DateTimeFormat("en-GB", options).format(new Date(value));
const venue = (item: Tournament) =>
  (item.venue as any)?.placeData?.name ||
  item.venue?.postcodeData?.postcode ||
  item.venue?.postcode ||
  "Venue TBC";
const enabled = (value: any) =>
  value?.status === "PUBLIC_AVAILABLE" || value?.status === "PROTECTED";
const mediaUrl = (value: string) =>
  /^(https?:)?\/\//i.test(value || "")
    ? value
    : `${window.apiBaseFile}${value || ""}`;
const QR = ({ value }: { value: string }) => {
  const [svg, setSvg] = useState("");
  useEffect(() => {
    QRCode.toString(value, { type: "svg", margin: 0 })
      .then(setSvg)
      .catch(() => setSvg(""));
  }, [value]);
  return <i className="unionTvQr" dangerouslySetInnerHTML={{ __html: svg }} />;
};

export const UnionBigScreen = ({ school }: { school: School }) => {
  const raw: any = school.publicBigscreenSite || {};
  const content = {
    welcome: true,
    today: true,
    nowNext: true,
    tournamentTv: true,
    recentResults: true,
    upcoming: true,
    news: true,
    sponsors: true,
    ...(raw.content || {})
  };
  const dwell = Math.max(6, Math.min(60, Number(raw.autoScrollingDelay) || 12));
  const refresh = Math.max(30, Number(raw.refreshInterval) || 300);
  const rowCap = [4, 5, 6].includes(Number(raw.rowCap))
    ? Number(raw.rowCap)
    : 6;
  const [tournaments, setTournaments] = useState<Tournament[]>([]);
  const [news, setNews] = useState<any[]>([]);
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [active, setActive] = useState(0);
  const [clock, setClock] = useState(new Date());
  const load = useCallback(
    () =>
      Promise.all([
        safe(getAllSchoolUnionTournaments(school.id), []),
        safe(
          getNews(school.id, {
            where: {},
            skip: 0,
            limit: 4,
            order: "date DESC"
          }),
          []
        ),
        safe(getSchoolBigscreenPhotos(school.id), [])
      ]).then(([items, stories, gallery]) => {
        setTournaments(
          items.filter(item => String(item.schoolId) === String(school.id))
        );
        setNews(stories);
        setPhotos(
          (gallery as Photo[]).filter(
            item => item.accessPreset === "PUBLIC" && item.picUrl
          )
        );
      }),
    [school.id]
  );
  useEffect(() => {
    load();
    const id = setInterval(load, refresh * 1000);
    return () => clearInterval(id);
  }, [load, refresh]);
  useEffect(() => {
    const id = setInterval(() => setClock(new Date()), 60000);
    return () => clearInterval(id);
  }, []);
  const slides = useMemo(() => {
    const now = new Date();
    const today = tournaments.filter(
      item => new Date(item.startTime).toDateString() === now.toDateString()
    );
    const ongoing = tournaments.filter(
      item =>
        new Date(item.startTime) <= now &&
        new Date(item.endTime || item.startTime) >= now
    );
    const future = tournaments
      .filter(item => new Date(item.startTime) > now)
      .sort((a, b) => +new Date(a.startTime) - +new Date(b.startTime));
    const past = tournaments
      .filter(item => new Date(item.startTime) < now)
      .sort((a, b) => +new Date(b.startTime) - +new Date(a.startTime));
    const tv = today.filter(
      item => item.domain && enabled(item.publicBigscreenSite)
    );
    const sponsors = Array.from(
      new Map(
        tournaments
          .flatMap(item => item.sponsors || [])
          .filter(item => item.name || item.picUrl)
          .map(item => [item.name || item.picUrl, item])
      ).values()
    );
    const candidates: Slide[] = [
      {
        id: "welcome",
        kind: "welcome",
        eyebrow: "Welcome",
        title: school.name,
        subtitle: "Tournament sport · Live",
        items: tournaments
      },
      {
        id: "today",
        kind: "tournaments",
        eyebrow: "Today",
        title: "Today's tournaments",
        subtitle: "Everything happening today",
        items: today
      },
      {
        id: "nowNext",
        kind: "tournaments",
        eyebrow: "Live programme",
        title: "Now & next",
        subtitle: "On now and coming up",
        items: [...ongoing, ...future].slice(0, rowCap)
      },
      {
        id: "tournamentTv",
        kind: "tv",
        eyebrow: "Watch live",
        title: "Tournament TV",
        subtitle: "Scan to follow scores and results",
        items: tv
      },
      {
        id: "recentResults",
        kind: "tournaments",
        eyebrow: "Latest",
        title: "Recent results",
        subtitle: "Recently completed tournaments",
        items: past.slice(0, rowCap)
      },
      {
        id: "upcoming",
        kind: "tournaments",
        eyebrow: "Coming up",
        title: "Upcoming tournaments",
        subtitle: "Published union competitions",
        items: future.slice(0, rowCap)
      },
      {
        id: "news",
        kind: "news",
        eyebrow: "Union news",
        title: "Latest news",
        subtitle: "Updates from the organisers",
        items: news
      },
      {
        id: "photos",
        kind: "photos",
        eyebrow: "Union gallery",
        title: "In pictures",
        subtitle: "Moments from across the union",
        items: photos
      },
      {
        id: "sponsors",
        kind: "sponsors",
        eyebrow: "With thanks",
        title: "Our sponsors",
        subtitle: "Partners supporting our published tournaments",
        items: sponsors
      }
    ];
    return candidates.filter(
      slide =>
        content[slide.id] !== false &&
        (slide.kind === "welcome" || slide.items.length)
    );
  }, [tournaments, news, photos, school.name, rowCap, JSON.stringify(content)]);
  useEffect(() => {
    setActive(value => Math.min(value, Math.max(slides.length - 1, 0)));
    if (raw.autoplay === false || slides.length < 2) return;
    const id = setInterval(
      () => setActive(value => (value + 1) % slides.length),
      dwell * 1000
    );
    return () => clearInterval(id);
  }, [slides.length, dwell, raw.autoplay]);
  const slide = slides[active];
  if (!slide) return null;
  const website = `${window.location.protocol}//${
    school.domain
  }.${window.location.hostname
    .split(".")
    .slice(1)
    .join(".")}/`;
  return (
    <main
      className="unionTv"
      data-scheme={String(raw.scheme || "MIDNIGHT").toLowerCase()}
    >
      <header>
        <div>
          {school.pic && (
            <i className="unionTvLogo">
              <img src={mediaUrl(school.pic)} alt="" />
            </i>
          )}
          <span>
            <strong>{school.name}</strong>
            <small>Tournament sport · Live</small>
          </span>
        </div>
        {raw.appearance?.showClock !== false && (
          <time>
            <strong>
              {fmt(clock.toISOString(), { hour: "2-digit", minute: "2-digit" })}
            </strong>
            <span>
              {fmt(clock.toISOString(), {
                weekday: "long",
                day: "numeric",
                month: "long"
              })}
            </span>
          </time>
        )}
      </header>
      <section>
        <div className="unionTvHeading">
          <span>{slide.eyebrow}</span>
          <h1>{slide.title}</h1>
          <p>{slide.subtitle}</p>
        </div>
        {slide.kind === "welcome" ? (
          <div className="unionTvWelcome">
            <h2>Everything happening across {school.name}.</h2>
            <div>
              <article>
                <strong>
                  {
                    tournaments.filter(
                      item => new Date(item.startTime) >= clock
                    ).length
                  }
                </strong>
                <span>Upcoming</span>
              </article>
              <article>
                <strong>
                  {
                    tournaments.filter(
                      item =>
                        new Date(item.startTime).toDateString() ===
                        clock.toDateString()
                    ).length
                  }
                </strong>
                <span>Today</span>
              </article>
              <article>
                <strong>
                  {new Set(tournaments.map(item => item.sportId)).size}
                </strong>
                <span>Sports</span>
              </article>
            </div>
          </div>
        ) : slide.kind === "tv" ? (
          <div className="unionTvLive">
            {slide.items.slice(0, 4).map(item => (
              <article key={item.id}>
                <QR value={getTournamentTVPublicSiteLink(item)} />
                <div>
                  <small>{item.sport?.name}</small>
                  <strong>{item.name}</strong>
                  <span>{item.domain}</span>
                </div>
              </article>
            ))}
          </div>
        ) : slide.kind === "news" ? (
          <div className="unionTvNews">
            {slide.items.slice(0, 3).map(item => (
              <article key={item.id}>
                <time>
                  {fmt(item.date, { day: "numeric", month: "short" })}
                </time>
                <strong>{item.title}</strong>
              </article>
            ))}
          </div>
        ) : slide.kind === "photos" ? (
          <div className="unionTvPhotos">
            {slide.items.slice(0, 6).map(item => (
              <figure key={item.id}>
                <img src={mediaUrl(item.picUrl)} alt="" />
                {item.description && (
                  <figcaption>{item.description}</figcaption>
                )}
              </figure>
            ))}
          </div>
        ) : slide.kind === "sponsors" ? (
          <div className="unionTvSponsors">
            {slide.items.slice(0, 8).map((item, index) => (
              <article key={index}>
                <div>
                  {item.picUrl ? (
                    <img src={mediaUrl(item.picUrl)} alt="" />
                  ) : (
                    <b>{item.name?.slice(0, 2)}</b>
                  )}
                  {item.link && <QR value={item.link} />}
                </div>
                <span>{item.name}</span>
                {item.link && <small>Scan to visit</small>}
              </article>
            ))}
          </div>
        ) : (
          <div className="unionTvRows">
            {slide.items.slice(0, rowCap).map(item => (
              <article key={item.id}>
                <time>
                  <strong>
                    {fmt(item.startTime, { day: "numeric", month: "short" })}
                  </strong>
                  <span>
                    {fmt(item.startTime, {
                      hour: "2-digit",
                      minute: "2-digit"
                    })}
                  </span>
                </time>
                <div>
                  <small>
                    {item.sport?.name}
                    {item.ages?.length
                      ? ` · ${getAgesGroupFormatted(
                          { ages: item.ages },
                          school
                        )}`
                      : ""}
                  </small>
                  <strong>{item.name}</strong>
                  <span>{venue(item)}</span>
                </div>
                {new Date(item.startTime).toDateString() ===
                  clock.toDateString() && <b>Today</b>}
              </article>
            ))}
          </div>
        )}
      </section>
      <footer>
        {raw.appearance?.showTicker !== false && (
          <div>
            <b>EXPLORE MORE</b>
            <span>
              Full tournaments, results and booking at {school.domain}
              .squadintouch.com
            </span>
          </div>
        )}
        <aside>
          <QR value={website} />
          <span>Scan for the union website</span>
        </aside>
        <nav>
          {slides.map((item, index) => (
            <i key={item.id} className={index === active ? "is-active" : ""} />
          ))}
        </nav>
      </footer>
    </main>
  );
};
